// Narrator director: watches GameState snapshots and decides WHEN the Keeper speaks and WHAT.
// Pure presentation — nothing here changes the game; the server stays authoritative.
// Rules (cooldowns, priorities, frequencies) follow docs/narrator/bible.md.
import { NARRATOR_LINES, type NarratorTrigger } from './lines.js';

export interface NarratorSnapshot {
  status: string;
  floor: number;
  coins: number;
  playerHp: number;
  playerMaxHp: number;
  lastEvent: string;
  lastEventDetail: string;
  rerollCount: number;
  rerollCost: number;
  equipChoices: string;
  mob: { id: string; alive: boolean; isBoss: boolean };
}

interface Persist { runs: number; best: number }
const STORE_KEY = 'caterva.narrator';
const loadPersist = (): Persist => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Persist>;
      return { runs: Number(p.runs) || 0, best: Number(p.best) || 0 };
    }
  } catch { /* private mode etc. */ }
  return { runs: 0, best: 0 };
};
const savePersist = (p: Persist): void => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
};

const GLOBAL_COOLDOWN_MS = 12_000;   // 20 s felt like silence in play (2026-08-26)
const pickOne = (arr: string[], avoid: string | undefined): string => {
  const pool = arr.length > 1 && avoid ? arr.filter((l) => l !== avoid) : arr;
  return pool[Math.floor(Math.random() * pool.length)] ?? arr[0] ?? '';
};
const chance = (p: number): boolean => Math.random() < p;

export class NarratorDirector {
  private persist = loadPersist();
  private runId = '';
  private lastSaidAt = 0;
  private lastLine = new Map<NarratorTrigger, string>();
  private used = new Map<NarratorTrigger, Set<string>>();   // lines already said this run, per trigger
  // per-run bookkeeping
  private said = new Set<string>();       // "trigger" or "trigger@floor" — fired once
  private lastStatus = '';
  private lastRerollCount = 0;
  private lastHp = 0;
  private hpLowFight = '';                // mob id where hp_low already fired
  private qteMisses = 0;
  private qteHits = 0;
  private lastEventKey = '';

  constructor(private readonly say: (text: string, opts: { hold?: number; where?: 'box' | 'gameover' }) => void) {}

  /** Start-of-run reset. Called when a fresh room binds. */
  newRun(runId: string): void {
    if (runId === this.runId) return;
    this.runId = runId;
    this.said.clear();
    this.lastStatus = ''; this.lastRerollCount = 0; this.lastHp = 0;
    this.hpLowFight = ''; this.qteMisses = 0; this.qteHits = 0; this.lastEventKey = '';
  }

  /** The line for the floor-entry pause (mob_intro). Returns '' when he stays quiet. */
  introLine(s: NarratorSnapshot): string {
    const floor = s.floor;
    let trig: NarratorTrigger | null = null;
    if (floor === 1) {
      trig = this.persist.runs === 0 ? 'run_start_first' : 'run_start';
      this.persist.runs += 1; savePersist(this.persist);
    } else if (s.mob.isBoss) trig = 'boss_enter';
    else if (floor === 5) trig = 'floor_5';
    else if (floor === 10) trig = 'floor_10';
    else if (floor === 15) trig = 'floor_15';
    else if (floor === 20) trig = 'floor_20';
    else if (floor === 30) trig = 'floor_30';
    else if (floor === 40) trig = 'floor_40';
    else if (floor === 50) trig = 'floor_50';
    else if (floor === 60) trig = 'floor_60';
    else if (chance(1 / 2)) trig = 'floor_enter';
    if (!trig) return '';
    const key = trig + '@' + String(floor);
    if (this.said.has(key)) return '';
    this.said.add(key);
    return this.fill(this.pick(trig), s);
  }

  /** Feed every snapshot. Speaks through `say` when a trigger fires. */
  onState(s: NarratorSnapshot): void {
    const now = Date.now();
    const fighting = s.status === 'fighting';
    const fresh = this.lastStatus === '';
    const evKey = s.lastEvent + '|' + s.lastEventDetail + '|' + String(s.floor) + '|' + s.mob.id;
    const newEvent = evKey !== this.lastEventKey;
    this.lastEventKey = evKey;

    if (fresh && fighting) this.said.add('first_blood');   // reloaded into a running fight

    let trig: NarratorTrigger | null = null;
    let hold: number | undefined;
    let where: 'box' | 'gameover' = 'box';

    // --- death (always speaks, wins over everything) ---
    if (s.status === 'ended' && !this.said.has('death')) {
      this.said.add('death');
      where = 'gameover';
      const record = s.floor > this.persist.best;
      if (record) { this.persist.best = s.floor; savePersist(this.persist); }
      if (s.mob.isBoss && s.mob.alive) trig = 'death_boss';
      else if (s.floor <= 2) trig = 'death_floor_1_2';
      else if (s.floor <= 6) trig = 'death_floor_3_6';
      else if (s.floor <= 11) trig = 'death_floor_7_11';
      else if (s.floor <= 24) trig = 'death_floor_12';
      else if (s.floor <= 49) trig = 'death_floor_25';
      else trig = 'death_floor_50';
      const text = (record && this.persist.runs > 1 ? this.fill(this.pick('death_record'), s) + ' ' : '') + this.fill(this.pick(trig), s);
      this.say(text, { where });
      this.remember(s);
      return;
    }
    if (s.status === 'ended') { this.remember(s); return; }

    // --- table (shop) ---
    if (s.status === 'floor_complete') {
      const shopKey = 'shop@' + String(s.floor);
      if (this.lastStatus !== 'floor_complete' && !this.said.has(shopKey)) {
        this.said.add(shopKey);
        if (s.mob.isBoss && !this.said.has('boss_kill')) { this.said.add('boss_kill'); trig = 'boss_kill'; }
        else trig = 'shop_open';   // every table (user call 2026-08-26)
        hold = 0;   // table lines stay until the table closes
      } else if (s.rerollCount > this.lastRerollCount) {
        const n = Math.min(3, s.rerollCount);
        trig = (['shop_reroll_1', 'shop_reroll_2', 'shop_reroll_3'] as const)[n - 1] ?? null;
        hold = 0;
      }
      this.lastRerollCount = s.rerollCount;
    } else {
      this.lastRerollCount = 0;
    }

    // --- the kill's drop roll: lands with the table (status floor_complete), so it shows on the table ---
    if (!trig && newEvent && !fresh && s.status === 'floor_complete' && s.lastEvent === 'mob_killed') {
      if (s.lastEventDetail.includes('no drop') && chance(2 / 3)) trig = 'kill_nodrop';
      else if (s.lastEventDetail.includes('JACKPOT') && chance(2 / 3)) trig = 'kill_jackpot';
      if (trig) hold = 0;
    }

    // --- fight ---
    if (!trig && fighting) {
      const hpPct = s.playerMaxHp > 0 ? s.playerHp / s.playerMaxHp : 1;
      if (s.lastEvent === 'player_attack' && !this.said.has('first_blood') && newEvent) {
        this.said.add('first_blood'); if (chance(2 / 3)) trig = 'first_blood';
      } else if (hpPct < 0.10 && !this.said.has('hp_critical')) {
        this.said.add('hp_critical'); trig = 'hp_critical';
      } else if (hpPct < 0.25 && this.hpLowFight !== s.mob.id && s.playerHp < this.lastHp) {
        this.hpLowFight = s.mob.id; trig = 'hp_low';
      } else if (s.coins >= 800 && !this.said.has('coins_rich')) {
        this.said.add('coins_rich'); trig = 'coins_rich';
      }
      if (newEvent && s.lastEvent === 'qte_hit') { this.qteHits += 1; if (this.qteHits === 5 && !trig) trig = 'qte_hit_5'; }
      if (newEvent && s.lastEvent === 'qte_miss') { this.qteMisses += 1; if (this.qteMisses === 3 && !trig) trig = 'qte_miss_3'; }
    }

    if (trig && trig !== 'shop_open' && trig !== 'boss_kill' && !trig.startsWith('shop_reroll')) {
      if (now - this.lastSaidAt < GLOBAL_COOLDOWN_MS) trig = null;   // he does not chatter
    }
    if (trig) {
      const opts: { hold?: number; where?: 'box' | 'gameover' } = { where };
      if (hold !== undefined) opts.hold = hold;
      this.say(this.fill(this.pick(trig), s), opts);
      this.lastSaidAt = now;
    }
    this.remember(s);
  }

  private remember(s: NarratorSnapshot): void {
    this.lastStatus = s.status; this.lastHp = s.playerHp;
  }

  private pick(trig: NarratorTrigger): string {
    const all = NARRATOR_LINES[trig];
    let used = this.used.get(trig);
    if (!used) { used = new Set(); this.used.set(trig, used); }
    let pool = all.filter((l) => !used.has(l));
    if (pool.length === 0) { used.clear(); pool = all; }   // whole repertoire said: start over
    const line = pickOne(pool, this.lastLine.get(trig));
    used.add(line);
    this.lastLine.set(trig, line);
    return line;
  }
  private fill(line: string, s: NarratorSnapshot): string {
    const title = s.floor >= 10 ? 'mine' : s.floor >= 5 ? 'tenant' : 'guest';
    return line
      .replace(/\{floor\}/g, String(s.floor))
      .replace(/\{coins\}/g, String(s.coins))
      .replace(/\{best\}/g, String(Math.max(1, this.persist.best)))
      .replace(/\{title\}/g, title);
  }
}
